/**
 * Catalog overlay: applies admin-edited overrides + admin-created new
 * products on top of the statically-imported JS arrays.
 *
 * The overlay is fetched once on app mount (and on demand) from
 * `GET /api/catalog/overrides`. We mutate the imported arrays in place so
 * existing consumers (`ALL_PRODUCTS`, `PRODUCTS`, `SHEIN_PRODUCTS`,
 * `getProductById`, etc.) keep working without any refactor — and the
 * change is then broadcast via a small subscription mechanism so React
 * components can re-render.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { PRODUCTS, ALL_PRODUCTS } from "@/data/products";
import { SHEIN_PRODUCTS } from "@/data/sheinProducts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CatalogContext = createContext(null);

// Snapshot of each product's original fields, indexed by id. Used to
// re-apply overrides cleanly when the overlay changes (so we don't double
// apply edits or leak previous values).
const ORIGINALS = new Map();

const _snapshotOriginal = (p) => {
  if (!ORIGINALS.has(p.id)) {
    // Shallow clone is enough — we only override scalars / arrays / nested
    // objects via Object.assign so the references are replaced wholesale.
    ORIGINALS.set(p.id, { ...p });
  }
};

const _restoreOriginals = () => {
  // For every product currently in ALL_PRODUCTS that has an original
  // snapshot, restore its scalar fields. We can't reassign the object
  // wholesale (consumers may hold references) but Object.assign in place
  // does the right thing.
  ALL_PRODUCTS.forEach((p) => {
    const orig = ORIGINALS.get(p.id);
    if (!orig) return;
    // Drop any keys that exist on `p` but not on `orig` (i.e. overlay-only
    // additions like `colors` we may have injected last cycle).
    Object.keys(p).forEach((k) => {
      if (!(k in orig)) delete p[k];
    });
    Object.assign(p, orig);
  });
};

// IDs of new (DB-only) products currently injected into ALL_PRODUCTS /
// PRODUCTS so we can remove them cleanly on the next refresh.
let CURRENT_NEW_IDS = [];
// IDs hidden by the current overlay.
let CURRENT_HIDDEN_IDS = new Set();

const _removeNewProducts = () => {
  if (CURRENT_NEW_IDS.length === 0) return;
  const drop = new Set(CURRENT_NEW_IDS);
  // Splice in place to preserve the exported reference.
  for (let i = PRODUCTS.length - 1; i >= 0; i--) if (drop.has(PRODUCTS[i].id)) PRODUCTS.splice(i, 1);
  for (let i = SHEIN_PRODUCTS.length - 1; i >= 0; i--) if (drop.has(SHEIN_PRODUCTS[i].id)) SHEIN_PRODUCTS.splice(i, 1);
  for (let i = ALL_PRODUCTS.length - 1; i >= 0; i--) if (drop.has(ALL_PRODUCTS[i].id)) ALL_PRODUCTS.splice(i, 1);
  CURRENT_NEW_IDS = [];
};

const _applyOverlay = (overlay) => {
  // 1) Reset to pristine state first so re-applying is idempotent.
  _removeNewProducts();
  _restoreOriginals();
  CURRENT_HIDDEN_IDS = new Set(overlay?.hidden_ids || []);

  // 2) Patch existing products (overrides).
  const overrides = overlay?.overrides || {};
  ALL_PRODUCTS.forEach((p) => {
    const patch = overrides[p.id];
    if (!patch) return;
    _snapshotOriginal(p);
    Object.assign(p, patch);
  });

  // 3) Mark hidden products with a non-enumerable flag the listings consult.
  //    Consumers filter using `isHiddenById()` below.

  // 4) Prepend new (DB-only) products so admins (and storefront visitors)
  //    see freshly-added items first instead of getting buried past the
  //    300-item slice in admin and behind hundreds of existing products on
  //    the storefront.
  const newProducts = overlay?.new_products || [];
  newProducts.forEach((np) => {
    const target = np.category === "shein" ? SHEIN_PRODUCTS : PRODUCTS;
    target.unshift(np);
    ALL_PRODUCTS.unshift(np);
    CURRENT_NEW_IDS.push(np.id);
  });
};

export const isHiddenById = (id) => CURRENT_HIDDEN_IDS.has(id);

export const CatalogProvider = ({ children }) => {
  // `version` bumps every time we re-apply the overlay. Listings include it
  // in their `useMemo` deps so they recompute and React re-renders.
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  // SHEIN sub-categories, fetched from /api/content (admin-managed). Falls
  // back to the static JS list when the request fails so the storefront
  // never blanks. Each entry: {id, name:{en,ar,fr}, is_hidden, sort_order}.
  const [sheinCategories, setSheinCategories] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [overridesRes, contentRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/catalog/overrides`).catch(() => null),
          fetch(`${BACKEND_URL}/api/content?platform=web`).catch(() => null),
        ]);
        const data = (overridesRes && overridesRes.ok) ? await overridesRes.json() : { overrides: {}, new_products: [], hidden_ids: [] };
        if (cancelled) return;
        _applyOverlay(data);
        if (contentRes && contentRes.ok) {
          try {
            const content = await contentRes.json();
            const remote = content?.shein_categories;
            if (Array.isArray(remote) && remote.length > 0) {
              setSheinCategories(remote);
            }
          } catch { /* keep null → static fallback */ }
        }
      } catch (_e) {
        // Swallow — site still works with the JS-shipped catalog.
      } finally {
        if (!cancelled) {
          setLoading(false);
          setVersion((v) => v + 1);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    try {
      const [overridesRes, contentRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/catalog/overrides`).catch(() => null),
        fetch(`${BACKEND_URL}/api/content?platform=web`).catch(() => null),
      ]);
      const data = (overridesRes && overridesRes.ok) ? await overridesRes.json() : { overrides: {}, new_products: [], hidden_ids: [] };
      _applyOverlay(data);
      if (contentRes && contentRes.ok) {
        const content = await contentRes.json();
        const remote = content?.shein_categories;
        if (Array.isArray(remote) && remote.length > 0) setSheinCategories(remote);
      }
      setVersion((v) => v + 1);
    } catch (_e) { /* ignore */ }
  };

  return (
    <CatalogContext.Provider value={{ version, loading, refresh, sheinCategories }}>
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog must be used within CatalogProvider");
  return ctx;
};
